import React, { ReactNode, useEffect, useState } from "react";
import { Colors } from "../../Constants/colors";
import { useMobile } from "../../hooks/deviceHooks/useDevice.hooks";
import { BtnSize, BtnType, Button } from "../Button";
import CrossIcon from "./../../images/Cross.svg";
import { createPortal } from "react-dom";
import "./Modal.css";

export enum ModalSize {
  xs = "xs",
  sm = "sm",
  md = "md",
  lg = "lg",
}
interface SubmissionButton {
  text?: string;
  onClick: () => unknown;
  isSubmitLoading?: boolean;
  buttonType?: BtnType;
}
interface Props {
  show: boolean;
  onClose: () => unknown;
  modalHeader?: string | null;
  children: ReactNode;
  footerComponent?: ReactNode;
  modalSize?: ModalSize;
  submissionButton?: SubmissionButton;
  requiresCloseIcon?: boolean;
}

const MODAL_WIDTH: Record<ModalSize, string> = {
  [ModalSize.xs]: "350px",
  [ModalSize.sm]: "400px",
  [ModalSize.md]: "600px",
  [ModalSize.lg]: "800px",
};

export const Modal = ({
  modalHeader = null,
  show,
  children,
  footerComponent,
  onClose,
  modalSize = ModalSize.sm,
  submissionButton = {
    isSubmitLoading: false,
    text: "Save",
    onClick: () => {},
    buttonType: BtnType.secondary,
  },
  requiresCloseIcon = true,
}: Props) => {
  const portalRoot = document.getElementById("app-root");

  const isMobile = useMobile();

  const [marginTop, setMarginTop] = useState(window.scrollY + "px");
  const [height, setHeight] = useState(window.innerHeight + "px");

  useEffect(() => {
    const handleResize = () => getWindowHeight();
    const handleScroll = () => handleMarginTop();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [show]);

  const getWindowHeight = () => {
    setHeight(window.innerHeight + "px");
  };

  const handleMarginTop = () => {
    setMarginTop(window.scrollY + "px");
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close the modal only if the click is outside the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!portalRoot) {
    return null; // or handle the absence of portalRoot as needed
  }

  return show ? (
    createPortal(
      <div
        className="rs-modal-overlay w-full flex flex-col justify-center items-center fixed top-0 left-0"
        style={{
          background: "rgba(17, 18, 22, 0.6)",
          backdropFilter: "blur(3px)",
          zIndex: 9999,
          height,
          marginTop,
          marginLeft: !isMobile ? "10%" : undefined,
        }}
        onClick={handleOverlayClick}
      >
        <div
          className="rs-modal-box rounded-none md:rounded-2xl lg:rounded-2xl flex flex-col"
          style={{
            backgroundColor: Colors.White,
            height: isMobile ? "100%" : undefined,
            maxHeight: isMobile ? "100%" : "88vh",
            width: isMobile ? "100%" : MODAL_WIDTH[modalSize],
            maxWidth: isMobile ? "100%" : "92vw",
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.45)",
          }}
        >
          {requiresCloseIcon ? (
            <button
              type="button"
              aria-label="Close"
              className="rs-modal-close-btn absolute top-4 right-4"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: Colors.VeryLightGrey,
                cursor: "pointer",
                zIndex: 1,
              }}
              onClick={onClose}
            >
              <img
                src={CrossIcon}
                alt="cross"
                style={{ width: "14px", height: "14px" }}
              />
            </button>
          ) : (
            <div />
          )}
          {modalHeader ? (
            <div
              className="Header text-2xl pr-14 pl-8"
              style={{
                paddingTop: "1.75rem",
                paddingBottom: "1.25rem",
                flexShrink: 0,
                borderBottom: `1px solid ${Colors.VeryLightGrey}`,
              }}
            >
              {modalHeader}
            </div>
          ) : (
            <div style={{ paddingTop: requiresCloseIcon ? "1.5rem" : 0 }} />
          )}

          <div
            className="rs-modal-body p-8 flex-1"
            style={{ overflowY: "auto", minHeight: 0 }}
          >
            {children}
          </div>

          {/*    THIS IS FOOTER FOR MODAL */}
          <div
            className="w-full px-8 py-5"
            style={{
              backgroundColor: Colors.White,
              flexShrink: 0,
              borderTop: `1px solid ${Colors.DarkOffWhite}`,
            }}
          >
            {footerComponent ? (
              footerComponent
            ) : (
              <div
                className="flex items-center"
                style={{
                  justifyContent:
                    modalSize === ModalSize.xs ? "center" : "flex-end",
                }}
              >
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      onClose();
                    }}
                    text="Cancel"
                    btnSize={BtnSize.sm}
                    btnType={BtnType.basic}
                  />

                  <Button
                    onClick={submissionButton.onClick}
                    text={submissionButton.text || "Save"}
                    btnSize={BtnSize.sm}
                    btnType={submissionButton.buttonType}
                    loading={submissionButton.isSubmitLoading}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>,
      portalRoot
    )
  ) : (
    <></>
  );
};
